import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Select,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import * as services from "../services/customer";
import Swal from "sweetalert2";
import provinces from "../provinces.json";

const CarFormDialog = ({
  open,
  onClose,
  onSuccess,
  customerData,
  initialData,
}) => {
  const isEdit = false;

  const [carType, setCarType] = useState(initialData?.type || "");
  const [carLicense, setCarLicense] = useState(initialData?.license || "");
  const [province, setProvince] = useState(initialData?.province || "");
  const [saving, setSaving] = useState(false);

  // เก็บข้อความ error รายช่อง
  const [errors, setErrors] = useState({
    carType: "",
    carLicense: "",
    province: "",
  });

  const resetForm = () => {
    setCarType("");
    setCarLicense("");
    setProvince("");
    setErrors({ carType: "", carLicense: "", province: "" });
    setSaving(false);
  };

  // provinces ให้ชัวร์ว่าเป็น array
  const provinceList = useMemo(() => {
    if (Array.isArray(provinces)) return provinces;
    if (provinces && typeof provinces === "object")
      return Object.values(provinces);
    return [];
  }, []);

  // canSubmit: ฟอร์มครบ + ทะเบียน ≥ 4 หลัก
  const canSubmit = useMemo(() => {
    const license = carLicense || "";
    return Boolean(carType.trim() && province.trim() && license.length >= 4);
  }, [carType, carLicense, province]);

  // validate ทั้งฟอร์ม
  const validate = () => {
    const next = { carType: "", carLicense: "", province: "" };
    let ok = true;

    if (!carType.trim()) {
      next.carType = "กรุณาเลือกประเภทรถ";
      ok = false;
    }
    const licenseDigits = (carLicense || "").replace(/\D/g, "");
    if (!licenseDigits) {
      next.carLicense = "กรุณากรอกทะเบียนรถ";
      ok = false;
    } else if (licenseDigits.length < 4) {
      next.carLicense = "ทะเบียนรถต้องมีอย่างน้อย 4 หลัก";
      ok = false;
    }
    if (!province.trim()) {
      next.province = "กรุณาเลือกจังหวัด";
      ok = false;
    }

    setErrors(next);
    return {
      ok,
      firstError: next.carType || next.carLicense || next.province || "",
    };
  };

  const handleSubmit = async () => {
    try {
      if (!customerData?.id) {
        await Swal.fire({
          icon: "error",
          title: "ไม่พบซัพพลายเออร์",
          text: "กรุณาลองใหม่อีกครั้ง",
          customClass: { container: "my-swal" },
        });
        return;
      }

      // ตรวจสอบฟอร์ม
      const { ok, firstError } = validate();
      if (!ok) {
        await Swal.fire({
          icon: "warning",
          title: "ข้อมูลไม่ถูกต้อง",
          text: firstError || "กรุณาตรวจสอบข้อมูลอีกครั้ง",
          customClass: { container: "my-swal" },
        });
        return;
      }

      setSaving(true);

      // รถใหม่
      const newCar = {
        type: carType.trim(),
        license: carLicense,
        province: province.trim(),
        customerId: customerData.id,
      };

      // รวมกับ cars เดิมอย่างปลอดภัย
      const updatedCars = [...(customerData?.cars || []), newCar];

      // อัปเดตครั้งเดียว
      const payload = { cars: updatedCars };
      const res = await services.updateCustomer(payload, customerData.id);

      // หา car ที่เพิ่งบันทึกกลับจาก response (ถ้ามี)
      const updatedCustomer = res?.data?.data || res?.data;
      const savedCar =
        updatedCustomer?.cars?.find(
          (c) =>
            c?.license === newCar.license &&
            c?.type === newCar.type &&
            c?.province === newCar.province
        ) || newCar;

      await Swal.fire({
        icon: "success",
        title: isEdit ? "แก้ไขรถสำเร็จ" : "เพิ่มรถสำเร็จ",
        customClass: { container: "my-swal" },
      });

      onSuccess?.(savedCar); // ส่งกลับให้ parent อัปเดตลิสต์ทันที
      resetForm();
      onClose?.();
    } catch (err) {
      console.error("Car save failed:", err);
      await Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถบันทึกรถได้ กรุณาลองใหม่",
        customClass: { container: "my-swal" },
      });
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={() => {
        resetForm();
        onClose?.();
      }}
      fullWidth
    >
      <DialogTitle>{isEdit ? "แก้ไขข้อมูลรถ" : "เพิ่มข้อมูลรถ"}</DialogTitle>

      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
      >
        <FormControl fullWidth sx={{ mt: 1 }} error={Boolean(errors.carType)}>
          <InputLabel>ประเภทรถ</InputLabel>
          <Select
            value={carType}
            label="ประเภทรถ"
            onChange={(e) => {
              setCarType(e.target.value);
              if (errors.carType) setErrors((p) => ({ ...p, carType: "" }));
            }}
          >
            <MenuItem value="รถกระบะ">รถกระบะ</MenuItem>
            <MenuItem value="รถเก๋ง">รถเก๋ง</MenuItem>
            <MenuItem value="รถพ่วงข้าง">รถพ่วงข้าง</MenuItem>
            <MenuItem value="รถ6ล้อ">รถ6ล้อ</MenuItem>
            <MenuItem value="รถ10ล้อ">รถ10ล้อ</MenuItem>
          </Select>
          <FormHelperText>{errors.carType || "เลือกประเภทรถ"}</FormHelperText>
        </FormControl>

        {/* ทะเบียนรถ: ตัวเลขล้วน + เปิดคีย์บอร์ดตัวเลขบนมือถือ */}
        <TextField
          label="ทะเบียนรถ"
          fullWidth
          value={carLicense}
          onChange={(e) => {
            setCarLicense(e.target.value);
            if (errors.carLicense) setErrors((p) => ({ ...p, carLicense: "" }));
          }}
          error={Boolean(errors.carLicense)}
          helperText={errors.carLicense || "กรอกทะเบียนรถอย่างน้อย 4 หลัก"}
        />

        <FormControl fullWidth error={Boolean(errors.province)}>
          <InputLabel>จังหวัด</InputLabel>
          <Select
            value={province}
            label="จังหวัด"
            onChange={(e) => {
              setProvince(e.target.value);
              if (errors.province) setErrors((p) => ({ ...p, province: "" }));
            }}
          >
            {provinceList.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.province || "เลือกจังหวัด"}</FormHelperText>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            resetForm();
            onClose?.();
          }}
          disabled={saving}
        >
          ยกเลิก
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={!canSubmit || saving} // กันกดซ้ำ/ฟอร์มยังไม่ครบ
        >
          {saving ? "กำลังบันทึก..." : isEdit ? "บันทึกการแก้ไข" : "บันทึก"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CarFormDialog;
